"use server"

import { sdk } from "@lib/config"
import { getAuthHeaders, getCartId } from "./cookies"
import type {
  RentalInfo,
  RentalAvailability,
  AddRentalToCartRequest
} from "@/types/rental"

/**
 * Fetch rental configuration for a product
 * Uses GET /store/products/:id/rental-info
 */
export async function getRentalInfo(
  productId: string,
  variantId?: string,
  currencyCode?: string
): Promise<RentalInfo | null> {
  try {
    const headers = await getAuthHeaders()
    const query: Record<string, string> = {}
    if (variantId) query.variantId = variantId
    if (currencyCode) query.currency_code = currencyCode

    const response = await sdk.client.fetch<RentalInfo>(
      `/store/products/${productId}/rental-info`,
      {
        method: "GET",
        query,
        headers,
        next: { revalidate: 300 },
      }
    )

    return response
  } catch (error) {
    console.error("Error fetching rental info:", error)
    return null
  }
}

/**
 * Check rental availability for date range
 * Uses GET /store/products/:id/rental-availability
 */
export async function checkRentalAvailability(
  productId: string,
  startDate: Date,
  endDate: Date,
  quantity: number = 1
): Promise<RentalAvailability | null> {
  try {
    const headers = await getAuthHeaders()

    const response = await sdk.client.fetch<RentalAvailability>(
      `/store/products/${productId}/rental-availability`,
      {
        method: "GET",
        query: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          quantity: quantity.toString(),
        },
        headers,
        cache: "no-store",
      }
    )

    return response
  } catch (error) {
    console.error("Error checking rental availability:", error)
    return null
  }
}

/**
 * Add rental item to cart
 * Uses POST /store/cart/:id/rental
 */
export async function addRentalToCart(
  data: AddRentalToCartRequest
): Promise<{ cart: any; rentalMetadata: any } | { error: string }> {
  try {
    const headers = await getAuthHeaders()
    const cartId = await getCartId()

    if (!cartId) {
      return { error: "No cart found. Please add items to cart first." }
    }

    const response = await sdk.client.fetch<{ cart: any; rentalMetadata: any }>(
      `/store/cart/${cartId}/rental`,
      {
        method: "POST",
        body: data,
        headers,
      }
    )

    return response
  } catch (error) {
    console.error("Error adding rental to cart:", error)
    return {
      error: error instanceof Error ? error.message : "Failed to add rental to cart"
    }
  }
}

/**
 * Get booked dates for a product within a date range
 * Returns array of dates that are not available
 */
export async function getBookedDates(
  productId: string,
  startDate: Date,
  endDate: Date
): Promise<Date[]> {
  try {
    const availability = await checkRentalAvailability(productId, startDate, endDate, 1)

    if (!availability) {
      return []
    }

    // Extract booked dates from inventory breakdown
    const bookedDates: Date[] = []

    for (const item of availability.inventory_breakdown) {
      if (item.status === 'unavailable' && item.current_booking) {
        const bookingStart = new Date(item.current_booking.start_date)
        const bookingEnd = new Date(item.current_booking.end_date)

        // Add all dates in the booking range
        const current = new Date(bookingStart)
        while (current <= bookingEnd) {
          bookedDates.push(new Date(current))
          current.setDate(current.getDate() + 1)
        }
      }
    }

    return bookedDates
  } catch (error) {
    console.error("Error getting booked dates:", error)
    return []
  }
}
